import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';

import { Stack, Text } from '@eluve/components';

import { useMedplum } from './MedplumProvider';
import {
  PATIENT_BY_IDENTIFIER_QUERY,
  PATIENT_BY_IDENTIFIER_QUERY_SOURCE,
} from './fhirQueries';

const ACCESS_NOTE: Record<
  'eluve-admin' | 'tenant-admin' | 'tenant-member',
  { label: string; detail: string }
> = {
  'eluve-admin': {
    label: 'Full access',
    detail:
      "As an Eluve admin, you can see every patient's FHIR record across every organization.",
  },
  'tenant-admin': {
    label: 'Full access in this organization',
    detail:
      "As an organization admin, you can see every patient's FHIR record in this organization.",
  },
  'tenant-member': {
    label: "You're a general practitioner for this patient",
    detail:
      "That's why you can see this record. You'll only see FHIR records for patients where you're listed as the GP.",
  },
};

/**
 * Always-on header for the Medical Profile: the access banner explaining why
 * this FHIR record is (or isn't) visible. The data views — Care Team,
 * Observations, Patient Uploads — render below it via the sub-tab Outlet.
 */
export const PatientProfile: React.FC = () => {
  const { patientId } = useParams() as { patientId: string };
  const { fhirClient, access } = useMedplum();

  const { data: patientData, loading: isPatientLoading } = useQuery(
    PATIENT_BY_IDENTIFIER_QUERY,
    {
      client: fhirClient ?? undefined,
      variables: { identifier: patientId },
      skip: !fhirClient,
      fetchPolicy: 'cache-and-network',
    },
  );

  const patient = patientData?.PatientList?.[0] ?? null;
  const hasAccess = Boolean(patient);

  if (isPatientLoading && !patientData) {
    return <Text>Checking access…</Text>;
  }

  const accessNote = access ? ACCESS_NOTE[access.reason] : null;

  if (!hasAccess) {
    return (
      <Stack gap={3} className="pt-2">
        <div className="rounded-md border border-dashed p-4">
          <Stack gap={2}>
            <Text weight="medium">No FHIR Patient visible</Text>
            <Text size="sm" color="tertiary">
              The GraphQL query below returned an empty list. This happens when
              either (a) the FHIR server's AccessPolicy scoped to your profile
              filters the Patient out — tenant-members see only patients where
              they are listed as the general practitioner — or (b) the Patient
              has not been synced into the FHIR store yet. The client cannot
              distinguish the two cases; both look like an empty list.
            </Text>
            <Text size="sm" color="tertiary">
              Operation executed against{' '}
              <code className="font-mono text-xs">/fhir/R4/$graphql</code>:
            </Text>
            <pre className="overflow-x-auto rounded-sm bg-bg-tertiary p-3 text-xs leading-relaxed">
              <code className="font-mono whitespace-pre">
                {PATIENT_BY_IDENTIFIER_QUERY_SOURCE}
              </code>
            </pre>
            <pre className="overflow-x-auto rounded-sm bg-bg-tertiary p-3 text-xs">
              <code className="font-mono">
                {'// variables\n'}
                {JSON.stringify({ identifier: patientId }, null, 2)}
              </code>
            </pre>
          </Stack>
        </div>
      </Stack>
    );
  }

  if (!accessNote) return null;

  return (
    <div className="rounded-md border border-border-secondary bg-bg-secondary px-3 py-2">
      <Stack gap={0}>
        <Text size="xs" weight="medium">
          {accessNote.label}
        </Text>
        <Text size="xs" color="tertiary">
          {accessNote.detail}
        </Text>
      </Stack>
    </div>
  );
};
